/**
 * Help panel (#7) — replaces the old top-of-screen hint strip with an
 * always-available "?" button (top-right) that toggles a center modal of
 * how-to-play instructions.
 *
 * Pure UI: owns its button + modal DOM, no game state. Mirrors the craft/tavern
 * modal factory shape ({ open, close, isOpen, toggle }) so main.ts can fold it
 * into the Esc-to-close chain. Content is authored from the live keybinds in
 * main.ts (right-click move/harvest/attack, V walk, Z build, QWER abilities, E
 * interact) plus the survival-MOBA gameplay loop.
 */

import { TREE_DEFS } from "./expertise-data";

export interface HelpPanel {
  toggle(): void;
  open(): void;
  close(): void;
  isOpen(): boolean;
}

interface Section {
  title: string;
  rows: Array<[string, string]>;
}

// Full 5-tier expertise trees — moved here out of the compact arc tooltip so the
// arc can stay focused on "what's next" (expertise-arc.ts). One section per tree.
const EXPERTISE_SECTIONS: Section[] = TREE_DEFS.map((tree) => ({
  title: `Expertise · ${tree.key}`,
  rows: tree.tiers.map(
    (t, i) => [`Tier ${i + 1} · ${t.name}`, t.effect] as [string, string],
  ),
}));

const SECTIONS: Section[] = [
  {
    title: "Move & camera",
    rows: [
      ["Right-click", "Move there — or harvest / attack what you click"],
      ["Mouse to screen edge", "Pan the camera across the map"],
      ["Space", "Recenter the camera on your kobold"],
      ["V", "Toggle walk mode — slower, but drains hunger & warmth less"],
    ],
  },
  {
    title: "Gather & fight",
    rows: [
      ["Right-click a tree / rock / bush", "Chop, mine, or forage it for materials"],
      ["Right-click an enemy or creature", "Attack it until it falls"],
      ["E (near a corpse)", "Loot the kill"],
    ],
  },
  {
    title: "Build (Z)",
    rows: [
      ["Z", "Open / close the build menu"],
      ["1–9, Q, W, E (build menu open)", "Pick a building, then left-click to place it"],
      ["Buildings", "Tent · Campfire · Palisade · Forge · Workbench · Hunter's Lodge · Storage · Tavern · Farm · Turret · Spike Trap · Wonder"],
    ],
  },
  {
    title: "Craft, cook & abilities",
    rows: [
      ["E near a Forge / Workbench", "Open the craft menu (weapons, armor, tools)"],
      ["E near a Campfire", "Cook food — fills a prep bar, then lands in your pack"],
      ["E near a Tavern", "Learn abilities at the spell shop"],
      ["Q / W / E / R", "Cast the ability in that slot"],
    ],
  },
  {
    title: "Pets",
    rows: [
      ["E near a Hunter's Lodge", "Tame a nearby wild animal (Sabertooth, Cave Bear, Nightwisp)"],
      ["F", "Pet command: Follow (pets engage enemies)"],
      ["H", "Pet command: Stay (pets hold position)"],
      ["G", "Heal Bear — active heal on your Cave Bear (requires Bear Shaman's Shroud; 70s cooldown)"],
      ["C", "Throw bait — nearby owned pets swarm the target (requires Obedience Rod in main-hand; 14s cooldown)"],
      ["X", "Cripple-throw — snare an enemy (requires Hunting Machete in off-hand; 10s cooldown)"],
      ["Hover pet portrait", "Show pet level, damage, HP and active mastery behaviors"],
    ],
  },
  {
    title: "Survive & win",
    rows: [
      ["Warmth", "Stay near a fire or you freeze and take damage"],
      ["Hunger", "Eat to stay Well-Fed (+10% attack) — going hungry never hurts you"],
      ["Expertise (left badges)", "Click a glowing badge to spend a level-up point. Each tree has 5 tiers — full trees below"],
      ["Goal", "Out-grow the enemy tribe — destroy their base / raise your Wonder"],
    ],
  },
];

function buildContent(): string {
  const sections = [...SECTIONS, ...EXPERTISE_SECTIONS].map((s) => {
    const rows = s.rows
      .map(
        ([key, desc]) =>
          `<div class="help-row"><span class="help-key">${key}</span><span class="help-desc">${desc}</span></div>`,
      )
      .join("");
    return `<div class="help-section"><div class="help-section__title">${s.title}</div>${rows}</div>`;
  });
  return sections.join("");
}

export function createHelpPanel(): HelpPanel {
  const btn = document.createElement("button");
  btn.id = "help-btn";
  btn.type = "button";
  btn.textContent = "?";
  btn.title = "How to play";
  document.body.appendChild(btn);

  const modal = document.createElement("div");
  modal.id = "help-modal";
  modal.style.display = "none";
  modal.innerHTML = `
    <div class="help-modal__hdr">
      <span>How to play</span>
      <span class="help-modal__close">[Esc] close</span>
    </div>
    <div class="help-modal__body">${buildContent()}</div>
  `;
  document.body.appendChild(modal);

  let opened = false;

  function open(): void {
    opened = true;
    modal.style.display = "flex";
  }
  function close(): void {
    opened = false;
    modal.style.display = "none";
  }
  function toggle(): void {
    if (opened) close();
    else open();
  }

  btn.addEventListener("click", toggle);
  modal.querySelector<HTMLSpanElement>(".help-modal__close")!.addEventListener("click", close);

  return { toggle, open, close, isOpen: () => opened };
}
